/** Portal navigation map, contextual help, and FAQ (Phase 1). */

import { looksLikeWriteCommand } from "assistant/commandFlow";

export const PORTAL_ROUTES = [
  { key: "dashboard", route: "/dashboard", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "assistant", route: "/assistant", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "home", route: "/accueil", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "teachers", route: "/enseignantes", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "affectations", route: "/affectations", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "establishments", route: "/establishments", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "statistics", route: "/statistiques", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "users", route: "/gestion-utilisateurs", roles: ["admin"] },
  { key: "leave_requests", route: "/leave-requests", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "recruiters", route: "/recruiters", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "area_attendance", route: "/synthese-presence-zone", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "establishment_attendance", route: "/rapport-presence-etablissement", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "teacher_license", route: "/teacher-license", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "notifications", route: "/notifications", roles: ["admin", "member", "observer", "auth", "other"] },
  { key: "settings", route: "/parametres", roles: ["superadmin"] },
  { key: "change_password", route: "/changer-mot-de-passe", roles: ["admin", "member", "observer", "auth", "other"] },
];

export const PAGE_HELP = {
  fr: {
    "/dashboard": {
      title: "Tableau de bord",
      summary: "Vue d'ensemble : KPI enseignants, tendances d'activité, recruteurs et congés en attente.",
      steps: [
        "Ajustez la plage de dates pour les tendances.",
        "Utilisez le rayon pour les statistiques régionales.",
        "Cliquez sur un recruteur pour voir ses enseignants.",
      ],
    },
    "/assistant": {
      title: "Assistant",
      summary: "Assistant portail en plein écran : recherche, fiches enseignantes, licences, présence et navigation.",
      steps: [
        "Posez une question ou utilisez les suggestions.",
        "Cliquez sur Réduire pour revenir au panneau flottant.",
        "La conversation est partagée entre le panneau et cette page.",
      ],
    },
    "/accueil": {
      title: "Accueil",
      summary: "Recherche rapide d'enseignants et accès à leur historique.",
      steps: ["Filtrez par nom ou matricule.", "Ouvrez l'historique depuis une ligne du tableau."],
    },
    "/enseignantes": {
      title: "Enseignantes",
      summary: "Annuaire complet avec pipeline d'inscription, formulaire d'ajout en haut de page et modification par ligne.",
      steps: [
        "Utilisez le pipeline (étapes d'inscription) pour filtrer par catégorie.",
        "Pour ajouter : développez le formulaire « Ajouter un nouvel enseignant » avec le bouton + en haut.",
        "Pour modifier : cliquez sur l'icône crayon sur la ligne concernée.",
        "Associez un recruteur dans le formulaire si nécessaire.",
      ],
    },
    "/affectations": {
      title: "Affectations",
      summary: "Assignez des enseignants à un établissement (province → département → sous-préfecture → établissement).",
      steps: [
        "Sélectionnez la hiérarchie géographique.",
        "Choisissez une ou plusieurs enseignantes.",
        "Indiquez la date d'affectation et enregistrez.",
      ],
    },
    "/establishments": {
      title: "Établissements",
      summary: "Gérez le répertoire des établissements scolaires.",
      steps: ["Ajoutez ou modifiez un établissement.", "Importez un CSV depuis Paramètres si besoin."],
    },
    "/statistiques": {
      title: "Statistiques",
      summary: "Analyses détaillées de présence et de couverture.",
      steps: ["Choisissez une date et un rayon.", "Exportez ou comparez les graphiques régionaux."],
    },
    "/gestion-utilisateurs": {
      title: "Gestion des utilisateurs",
      summary: "Création et administration des comptes portail (admin uniquement).",
      steps: [
        "Définissez le rôle : admin, agent de saisie, observateur.",
        "Assignez provinces et départements autorisés.",
      ],
    },
    "/leave-requests": {
      title: "Demandes de congé",
      summary: "Soumettez, suivez et approuvez les demandes de congé.",
      steps: [
        "Créez une demande avec type et dates.",
        "Les admins peuvent changer le statut (En attente, Approuvé, Rejeté).",
      ],
    },
    "/recruiters": {
      title: "Recruteurs",
      summary: "Gérez les recruteurs et consultez leurs enseignants.",
      steps: ["Ajoutez un recruteur avec coordonnées.", "Ouvrez la liste enseignants depuis Actions."],
    },
    "/synthese-presence-zone": {
      title: "Synthèse présence zone",
      summary: "Résumé de présence agrégé par province et département sur une période.",
      steps: [
        "Choisissez la date de début, la date de fin et le nombre minimum de jours de présence.",
        "Cliquez sur Rechercher pour charger le tableau par zone.",
        "Consultez le taux de vérification (enseignants vérifiés / affectés) par département.",
      ],
    },
    "/rapport-presence-etablissement": {
      title: "Rapport présence établissement",
      summary: "Présence détaillée par enseignant, établissement et calendrier journalier.",
      steps: [
        "Sélectionnez province → département → sous-préfecture → établissement (optionnel).",
        "Indiquez la plage de dates et le rayon en mètres, puis générez le rapport.",
        "Développez une ligne pour voir le calendrier de présence jour par jour.",
      ],
    },
    "/teacher-license": {
      title: "Licences enseignants",
      summary: "Activez les enseignants inscrits : seuls les licenciés peuvent se connecter et pointer.",
      steps: [
        "Consultez le quota (licenciés / max / restants).",
        "Filtrez par statut ou zone géographique.",
        "Activez ou désactivez un enseignant (ou un lot) selon les créneaux disponibles.",
      ],
    },
    "/notifications": {
      title: "Notifications",
      summary: "Historique et envoi de notifications ciblées.",
      steps: ["Consultez l'historique.", "Envoyez une notification par zone ou établissement."],
    },
    "/parametres": {
      title: "Paramètres",
      summary: "Langue, import CSV établissements et préférences.",
      steps: ["Changez la langue FR/EN.", "Importez des établissements via CSV."],
    },
    "/changer-mot-de-passe": {
      title: "Changer le mot de passe",
      summary: "Mettez à jour le mot de passe de votre compte portail.",
      steps: [
        "Saisissez votre mot de passe actuel.",
        "Entrez le nouveau mot de passe et confirmez-le.",
        "Enregistrez — vous resterez connecté avec le nouveau mot de passe.",
      ],
    },
  },
  en: {
    "/dashboard": {
      title: "Dashboard",
      summary: "Overview: teacher KPIs, activity trends, recruiters, and pending leave requests.",
      steps: [
        "Adjust the date range for trends.",
        "Use radius for regional statistics.",
        "Click a recruiter to view their teachers.",
      ],
    },
    "/assistant": {
      title: "Assistant",
      summary: "Full-screen portal assistant: search, teacher profiles, licenses, attendance, and navigation.",
      steps: [
        "Ask a question or use the suggestion chips.",
        "Click Collapse to return to the floating panel.",
        "Conversation is shared between the panel and this page.",
      ],
    },
    "/accueil": {
      title: "Home",
      summary: "Quick teacher search and access to history.",
      steps: ["Filter by name or registration number.", "Open history from a table row."],
    },
    "/enseignantes": {
      title: "Teachers",
      summary: "Full directory with enrollment pipeline, add form at the top, and per-row edit actions.",
      steps: [
        "Use the enrollment pipeline chips to filter by category.",
        "To add: expand the « Add new teacher » form with the + button at the top.",
        "To edit: click the pencil icon on the teacher row.",
        "Link a recruiter in the form when needed.",
      ],
    },
    "/affectations": {
      title: "Assignments",
      summary: "Assign teachers to an establishment (province → department → sub-prefecture → establishment).",
      steps: [
        "Select the geographic hierarchy.",
        "Choose one or more teachers.",
        "Set the assignment date and save.",
      ],
    },
    "/establishments": {
      title: "Establishments",
      summary: "Manage the school establishment directory.",
      steps: ["Add or edit an establishment.", "Import CSV from Settings if needed."],
    },
    "/statistiques": {
      title: "Statistics",
      summary: "Detailed attendance and coverage analytics.",
      steps: ["Pick a date and radius.", "Compare regional charts."],
    },
    "/gestion-utilisateurs": {
      title: "User management",
      summary: "Create and administer portal accounts (admin only).",
      steps: [
        "Set role: admin, data entry agent, observer.",
        "Assign allowed provinces and departments.",
      ],
    },
    "/leave-requests": {
      title: "Leave requests",
      summary: "Submit, track, and approve leave requests.",
      steps: [
        "Create a request with type and dates.",
        "Admins can change status (Pending, Approved, Rejected).",
      ],
    },
    "/recruiters": {
      title: "Recruiters",
      summary: "Manage recruiters and view their teachers.",
      steps: ["Add a recruiter with contact details.", "Open the teacher list from Actions."],
    },
    "/synthese-presence-zone": {
      title: "Area attendance summary",
      summary: "Attendance aggregated by province and department over a date range.",
      steps: [
        "Set start date, end date, and minimum days present.",
        "Click Search to load the zone table.",
        "Review check rate (teachers checked / assigned) per department.",
      ],
    },
    "/rapport-presence-etablissement": {
      title: "Establishment attendance report",
      summary: "Per-teacher attendance with daily calendar and radius check.",
      steps: [
        "Select province → department → sub-prefecture → establishment (optional).",
        "Set date range and radius in meters, then generate the report.",
        "Expand a row to see day-by-day attendance calendar.",
      ],
    },
    "/teacher-license": {
      title: "Teacher licenses",
      summary: "Activate registered teachers — only licensed teachers can sign in and check in.",
      steps: [
        "Review quota (licensed / max / remaining).",
        "Filter by status or geography.",
        "Activate or deactivate teachers (or batches) when slots are available.",
      ],
    },
    "/notifications": {
      title: "Notifications",
      summary: "History and targeted notification sending.",
      steps: ["Review history.", "Send notifications by zone or establishment."],
    },
    "/parametres": {
      title: "Settings",
      summary: "Language, establishment CSV import, and preferences.",
      steps: ["Switch language FR/EN.", "Import establishments via CSV."],
    },
    "/changer-mot-de-passe": {
      title: "Change password",
      summary: "Update your portal account password.",
      steps: [
        "Enter your current password.",
        "Type the new password and confirm it.",
        "Save — you will stay signed in with the new password.",
      ],
    },
  },
};

/** Queries that look like FAQ but should stay on other handlers (guides, live data, navigation). */
const FAQ_EXCLUDE_PATTERNS = [
  /\b(guide|wizard|pas a pas|pas à pas|step by step|etapes|étapes)\b/,
  /\b(help on this page|aide sur cette page|this page|cette page|current page|page actuelle)\b/,
  /\b(portal summary|resume du portail|résumé du portail|pending leave|congés en attente|conges en attente)\b/,
  /\b(what can you|what needs attention|license quota|quota licence|area attendance|establishment attendance report)\b/,
  /\b(find a teacher|help me find|look up a teacher|aide-moi à trouver|how many|combien)\b/,
  /\b(open |go to |ouvrir |naviguer |take me to |show me the )\b/,
];

export const FAQ_ENTRIES = {
  fr: [
    {
      id: "faq-password",
      keywords: ["mot de passe", "password", "changer", "oublie", "reset"],
      question: "Comment changer mon mot de passe ?",
      answer:
        "Allez dans le menu utilisateur → Changer le mot de passe, ou demandez-moi d'y naviguer. Saisissez l'ancien et le nouveau mot de passe.",
      route: "/changer-mot-de-passe",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-affectation",
      keywords: ["affectation", "affecter", "assigner", "établissement", "assign"],
      question: "Comment créer une affectation ?",
      answer:
        "Sur Affectations : choisissez province → département → sous-préfecture → établissement, sélectionnez les enseignantes et la date, puis enregistrez.",
      route: "/affectations",
      roles: ["admin", "member", "auth", "other"],
    },
    {
      id: "faq-leave",
      keywords: ["congé", "conge", "leave", "absence", "demande"],
      question: "Comment soumettre une demande de congé ?",
      answer:
        "Ouvrez Demandes de congé, cliquez sur Ajouter, choisissez l'enseignante, le type et les dates, puis validez.",
      route: "/leave-requests",
      roles: ["admin", "member", "auth", "other"],
    },
    {
      id: "faq-attendance",
      keywords: ["présence", "presence", "attendance", "rapport", "report"],
      question: "Où voir les rapports de présence ?",
      answer:
        "Deux pages : Synthèse présence zone (agrégé par zone) et Rapport présence établissement (détail par établissement).",
      route: "/rapport-presence-etablissement",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-users",
      keywords: ["utilisateur", "user", "compte", "admin", "créer compte", "create user", "create account", "creer utilisateur", "créer utilisateur"],
      question: "Comment créer un utilisateur portail ?",
      answer:
        "Réservé aux administrateurs : Gestion des utilisateurs → Ajouter. Définissez rôle, provinces et départements.",
      route: "/gestion-utilisateurs",
      roles: ["admin"],
    },
    {
      id: "faq-language",
      keywords: ["langue", "language", "english", "français", "francais"],
      question: "Comment changer la langue ?",
      answer: "Paramètres → basculez entre Français et English. Le choix est mémorisé.",
      route: "/parametres",
      roles: ["superadmin"],
    },
    {
      id: "faq-map",
      keywords: ["carte", "map", "géolocalisation", "gps"],
      question: "Où est la carte des établissements ?",
      answer:
        "Depuis Accueil ou le menu, accédez à la vue carte pour visualiser les établissements géolocalisés.",
      route: "/accueil",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-everifpro",
      keywords: ["everifpro", "e-verif", "c est quoi", "qu est ce", "presentation", "présentation", "a quoi sert", "à quoi sert"],
      question: "Qu'est-ce qu'eVerifPro ?",
      answer:
        "eVerifPro est le portail de suivi des présences des enseignants : annuaire, affectations, congés, rapports de présence, licences mobile et administration des comptes.",
      route: "/dashboard",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-license-flow",
      keywords: ["licence mobile", "licence app", "application mobile", "inscription mobile", "activer enseignant", "licence workflow"],
      question: "Comment fonctionne la licence enseignant ?",
      answer:
        "L'enseignant s'inscrit via l'application mobile (non licencié au départ). Un administrateur active la licence dans Licences enseignants — seuls les licenciés peuvent se connecter et pointer.",
      route: "/teacher-license",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-leave-approve",
      keywords: ["approuver conge", "approuver congé", "valider conge", "valider congé", "approve leave", "reject leave", "rejeter conge"],
      question: "Qui peut approuver les congés ?",
      answer:
        "Les utilisateurs avec droits d'écriture peuvent soumettre des demandes ; les administrateurs et agents autorisés peuvent approuver ou rejeter depuis Demandes de congé.",
      route: "/leave-requests",
      roles: ["admin", "member", "auth", "other"],
    },
    {
      id: "faq-attendance-diff",
      keywords: ["difference presence", "différence présence", "zone vs etablissement", "synthese vs rapport", "area vs establishment"],
      question: "Différence entre synthèse zone et rapport établissement ?",
      answer:
        "Synthèse présence zone : agrégé par province/département sur une période. Rapport établissement : détail par enseignant et calendrier journalier pour un établissement.",
      route: "/synthese-presence-zone",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-csv",
      keywords: ["csv", "import", "importer etablissement", "importer établissement", "import establishments"],
      question: "Comment importer des établissements en CSV ?",
      answer:
        "Paramètres → import CSV des établissements. Utilisez le modèle fourni, puis vérifiez la liste dans Établissements.",
      route: "/parametres",
      roles: ["superadmin"],
    },
    {
      id: "faq-roles",
      keywords: ["role", "rôle", "observateur", "observer", "agent saisie", "administrateur", "permissions", "droits"],
      question: "Quels sont les rôles portail ?",
      answer:
        "Administrateur : accès complet. Agent de saisie : écriture sur les modules autorisés. Observateur : lecture seule (consultation, rapports). Les droits dépendent du compte créé en Gestion des utilisateurs.",
      route: "/gestion-utilisateurs",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
  ],
  en: [
    {
      id: "faq-password",
      keywords: ["password", "change", "reset", "forgot", "change password", "how to change", "how to change password"],
      question: "How do I change my password?",
      answer:
        "Go to user menu → Change password, or ask me to navigate there. Enter current and new password.",
      route: "/changer-mot-de-passe",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-affectation",
      keywords: ["assignment", "assign", "establishment", "affectation"],
      question: "How do I create an assignment?",
      answer:
        "On Assignments: pick province → department → sub-prefecture → establishment, select teachers and date, then save.",
      route: "/affectations",
      roles: ["admin", "member", "auth", "other"],
    },
    {
      id: "faq-leave",
      keywords: ["leave", "absence", "request", "congé"],
      question: "How do I submit a leave request?",
      answer:
        "Open Leave requests, click Add, choose teacher, type and dates, then submit.",
      route: "/leave-requests",
      roles: ["admin", "member", "auth", "other"],
    },
    {
      id: "faq-attendance",
      keywords: ["attendance", "presence", "report"],
      question: "Where are attendance reports?",
      answer:
        "Two pages: Area attendance summary (by zone) and Establishment attendance report (by school).",
      route: "/rapport-presence-etablissement",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-users",
      keywords: ["user", "account", "admin", "create account", "create a user", "how to create a user", "how to create user"],
      question: "How do I create a portal user?",
      answer:
        "Admins only: User management → Add. Set role, provinces, and departments.",
      route: "/gestion-utilisateurs",
      roles: ["admin"],
    },
    {
      id: "faq-language",
      keywords: ["language", "langue", "english", "french"],
      question: "How do I change the language?",
      answer: "Settings → switch between French and English. Your choice is saved.",
      route: "/parametres",
      roles: ["superadmin"],
    },
    {
      id: "faq-map",
      keywords: ["map", "carte", "geolocation", "gps"],
      question: "Where is the establishments map?",
      answer: "From Home or the menu, open the map view to see geolocated establishments.",
      route: "/accueil",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-everifpro",
      keywords: ["everifpro", "e-verif", "what is", "what's", "about", "purpose", "what does"],
      question: "What is eVerifPro?",
      answer:
        "eVerifPro is the teacher attendance portal: directory, assignments, leave, attendance reports, mobile licenses, and user administration.",
      route: "/dashboard",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-license-flow",
      keywords: ["mobile license", "mobile app", "app registration", "activate teacher", "license workflow"],
      question: "How does teacher licensing work?",
      answer:
        "Teachers register via the mobile app (unlicensed at first). An admin activates the license under Teacher licenses — only licensed teachers can sign in and check in.",
      route: "/teacher-license",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-leave-approve",
      keywords: ["approve leave", "reject leave", "validate leave", "who approves"],
      question: "Who can approve leave requests?",
      answer:
        "Users with write access can submit requests; admins and authorized agents approve or reject from Leave requests.",
      route: "/leave-requests",
      roles: ["admin", "member", "auth", "other"],
    },
    {
      id: "faq-attendance-diff",
      keywords: ["difference attendance", "area vs establishment", "zone vs school", "summary vs report"],
      question: "Area summary vs establishment report?",
      answer:
        "Area attendance summary: aggregated by province/department over a period. Establishment report: per-teacher detail and daily calendar for one school.",
      route: "/synthese-presence-zone",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
    {
      id: "faq-csv",
      keywords: ["csv", "import", "import establishments", "bulk establishments"],
      question: "How do I import establishments from CSV?",
      answer: "Settings → establishment CSV import. Use the provided template, then review the list under Establishments.",
      route: "/parametres",
      roles: ["superadmin"],
    },
    {
      id: "faq-roles",
      keywords: ["role", "observer", "read only", "permissions", "admin", "data entry"],
      question: "What are the portal roles?",
      answer:
        "Admin: full access. Data entry agent: write on authorized modules. Observer: read-only (reports, lists). Rights are set when creating the account in User management.",
      route: "/gestion-utilisateurs",
      roles: ["admin", "member", "observer", "auth", "other"],
    },
  ],
};

export const NAV_ALIASES = {
  fr: {
    dashboard: ["dashboard", "tableau de bord", "tableau", "accueil admin"],
    assistant: ["assistant", "aide ia", "chat assistant", "assistant plein ecran"],
    home: ["accueil", "home", "recherche"],
    teachers: ["enseignantes", "enseignant", "professeur", "teachers", "teacher", "open teachers", "teacher directory"],
    affectations: ["affectations", "affectation", "assigner", "assignments", "open assignments"],
    establishments: ["établissements", "etablissements", "establishments", "école", "ecole"],
    statistics: ["statistiques", "stats", "statistics"],
    users: ["utilisateurs", "gestion utilisateurs", "users", "comptes"],
    leave_requests: ["congés", "conges", "congé", "leave", "demandes de congé", "demandes de conge", "open leave"],
    recruiters: ["recruteurs", "recruteur", "recruiters"],
    area_attendance: ["synthèse présence", "synthese presence", "zone", "area attendance"],
    establishment_attendance: ["rapport présence", "rapport presence", "présence établissement"],
    teacher_license: ["licences", "licence enseignant", "licences enseignants", "teacher license", "licenses"],
    notifications: ["notifications", "notification", "alertes"],
    settings: ["paramètres", "parametres", "settings", "réglages"],
    change_password: ["mot de passe", "password", "changer mot de passe"],
  },
  en: {
    dashboard: ["dashboard"],
    assistant: ["assistant", "ai help", "assistant chat", "full screen assistant"],
    home: ["home", "accueil"],
    teachers: ["teachers", "teacher", "enseignantes", "open teachers", "teacher directory"],
    affectations: ["assignments", "assignment", "affectations", "open assignments"],
    establishments: ["establishments", "establishment", "schools"],
    statistics: ["statistics", "stats", "statistiques"],
    users: ["users", "user management", "accounts"],
    leave_requests: ["leave", "leave requests", "congé", "open leave", "leave page"],
    recruiters: ["recruiters", "recruiter"],
    area_attendance: ["area attendance", "zone summary"],
    establishment_attendance: ["establishment attendance", "attendance report"],
    teacher_license: ["teacher licenses", "licenses", "licence quota", "license quota"],
    notifications: ["notifications", "alerts"],
    settings: ["settings", "parameters"],
    change_password: ["password", "change password"],
  },
};

function normalize(text) {
  return String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function scoreFaqEntry(query, entry) {
  const q = normalize(query);
  let score = 0;
  if (normalize(entry.question).includes(q)) score += 3;
  if (normalize(entry.answer).includes(q)) score += 1;
  entry.keywords.forEach((kw) => {
    const n = normalize(kw);
    if (q.includes(n) || n.includes(q)) score += 2;
  });
  return score;
}

/** True when the query should be answered from FAQ (not teacher search or generic page help). */
export function isFaqQuery(query, language, roleBucket) {
  const q = normalize(query);
  if (!q || looksLikeWriteCommand(query) || FAQ_EXCLUDE_PATTERNS.some((re) => re.test(q))) return false;

  const entries = FAQ_ENTRIES[language] || FAQ_ENTRIES.fr;
  let bestScore = 0;
  entries
    .filter((e) => e.roles.includes(roleBucket))
    .forEach((entry) => {
      bestScore = Math.max(bestScore, scoreFaqEntry(query, entry));
    });

  return bestScore >= 2;
}

export function searchFaq(query, language, roleBucket) {
  const entries = FAQ_ENTRIES[language] || FAQ_ENTRIES.fr;
  const q = normalize(query);
  if (!q) return [];

  return entries
    .filter((e) => e.roles.includes(roleBucket))
    .map((entry) => ({ entry, score: scoreFaqEntry(query, entry) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(({ entry }) => entry);
}

export function resolveRouteKey(query, language) {
  const aliases = NAV_ALIASES[language] || NAV_ALIASES.fr;
  const q = normalize(query);
  let best = null;
  let bestScore = 0;

  Object.entries(aliases).forEach(([key, terms]) => {
    terms.forEach((term) => {
      const n = normalize(term);
      if (q.includes(n) && n.length >= bestScore) {
        best = key;
        bestScore = n.length;
      }
    });
  });

  return best;
}

export function getPageHelp(pathname, language) {
  const lang = PAGE_HELP[language] ? language : "fr";
  const base = pathname.split("/:")[0];
  return PAGE_HELP[lang][base] || null;
}

export function getRouteByKey(key, roleBucket) {
  const route = PORTAL_ROUTES.find((r) => r.key === key);
  if (!route || !route.roles.includes(roleBucket)) return null;
  return route;
}
